import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region redux
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "store";
import { loadCompanyDataAsync, updateBusinessUnitManagerSettings } from "store/actions/authActions";
import { displayDialog, displayErrorMessage } from "store/actions/utilActions";
import { ThunkDispatch } from "redux-thunk";
//#endregion
//#region types
import { SettingsFormTypes } from "models/formTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { EmployeeSettingsFormContainerProps } from "models/propTypes/settingsPropTypes";
import { FormikProps } from "formik/dist/types";
import { CommonConstants, FormFieldConstants, IconDefinitionConstants, SettingsConstants } from "utils/constants";
import { WintSuperkollAPIModel, WintSuperkollAPIModelWintSalary } from "wint-api-client/dist/types";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { TranslationKeys } from "translations";
import { EmployeeType } from "models/enums/employee";
import { DialogProps } from "models/propTypes/dialogPropTypes";
import { DialogEnums } from "models/enums";
import { DisplayType } from "models/enums/style";
//#endregion
//#region components
import EmployeeForm from "./employeeForm";
import useEmployeeHelpers from "../useEmployeeHelpers";
import { Skeletons } from "components/common";
import { useNavigate } from "react-router-dom";
import { useEmployeeService, useSettingsService, useSalaryService, useCompanyService } from "serviceHooks";
import useScheduleChangeDialog from "./useScheduleChangeDialog";
import useSalaryPermissions from "components/salary/hooks/useSalaryPermissions";
import useSalaryReApproveDialog from "components/wintSalary/useSalaryReApproveDialog";
import { generateDefaultScheduleWeek } from "../schedule";
import { scheduleMatchesExpectedWorkTime, ScheduleWeekHours } from "./scheduleWorkTimeValidation";
//#endregion
import { BusinessUnitService } from "services";
import * as Yup from "yup";
import {
  DateHelper,
  LocalizationHelperInstance,
  PersonNumberHelper,
  RegularExpressions,
  UrlHelper,
  UtilsHelper,
} from "utils/helpers";
import { terminationReasonLabel } from "utils/helpers/terminationReasonHelper";
import { isEmploymentEnded, isRehireStartDate, isSameDay } from "utils/helpers/rehireHelper";

// Local type extension for temporary vacation compensation field
type EmployeeSettingsWithTemp = SettingsFormTypes.EmployeeSettings & {
  tempVacationCompensation?: number;
  taxAdjustment?: number;
};

// Named constants for bitmask fields - makes the code more self-documenting
const VACATION_DAYS_BIT = WintSuperkollAPIModelWintSalary.WintSalaryOptionallyRequiredFields.VacationDays;
const VACATION_COMPENSATION_BIT =
  WintSuperkollAPIModelWintSalary.WintSalaryOptionallyRequiredFields.VacationCompensation;
const SCHEDULE_BIT = WintSuperkollAPIModelWintSalary.WintSalaryOptionallyRequiredFields.Schedule;
const MONTHLY_GROSS_SALARY_BIT = WintSuperkollAPIModelWintSalary.WintSalaryOptionallyRequiredFields.MonthlyGrossSalary;
const HOURLY_GROSS_SALARY_BIT = WintSuperkollAPIModelWintSalary.WintSalaryOptionallyRequiredFields.HourlyGrossSalary;

// Schedule validation codes from Wint.Salary: suppressed from the generic error toast and
// mapped to client-localized messages in saveEmployee's catch. The frozen-norm code is only
// returned by servers that predate the freeze removal; kept as a localized fallback.
const SCHEDULE_VALIDATION_ERROR_MESSAGES: Record<string, string> = {
  ScheduleStandardWorkTimeCannotBeChanged: TranslationKeys.Settings.ScheduleStandardWorkTimeCannotBeChanged,
  ScheduleWorkTimeMismatch: TranslationKeys.Settings.ScheduleWorkTimeMismatch,
};

// Convert schedule week from minutes (backend) to hours (frontend)
const convertScheduleWeekToHours = (week: any, index: number) => ({
  week: index,
  monday: week.monday / 60,
  tuesday: week.tuesday / 60,
  wednesday: week.wednesday / 60,
  thursday: week.thursday / 60,
  friday: week.friday / 60,
  saturday: week.saturday / 60,
  sunday: week.sunday / 60,
});

// Convert schedule week from hours (frontend) to minutes (backend)
const convertScheduleWeekToMinutes = (week: any) => ({
  week: week.week,
  monday: week.monday * 60,
  tuesday: week.tuesday * 60,
  wednesday: week.wednesday * 60,
  thursday: week.thursday * 60,
  friday: week.friday * 60,
  saturday: week.saturday * 60,
  sunday: week.sunday * 60,
});

const getTerminationReasonName = (reason?: WintSuperkollAPIModel.EmploymentTerminationReason): string =>
  reason == null || reason === WintSuperkollAPIModel.EmploymentTerminationReason.Undefined
    ? ""
    : terminationReasonLabel(reason);

const EmployeeFormContainer: ForwardRefRenderFunction<FormHandles, EmployeeSettingsFormContainerProps> = (
  props,
  ref
) => {
  const salaryService = useSalaryService();
  const settingsService = useSettingsService();
  const employeeService = useEmployeeService();
  const companyService = useCompanyService();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [availableSalaryTypes, setAvailableSalaryTypes] = React.useState<
    WintSuperkollAPIModelWintSalary.WintSalaryType[]
  >([]);
  const [userTypesEnabled, setUserTypesEnabled] = React.useState<boolean>(false);
  const [enabledUserTypes, setEnabledUserTypes] = React.useState<WintSuperkollAPIModel.UserTypeDto[]>([]);
  const { employee } = props;
  const assistantsActive = useSelector((state: AppState) => state.auth.selectedCompany.isAssistantsActive);
  const selectedCompanyId = useSelector((state: AppState) => state.auth.selectedCompany.id);
  const loggedInUserId = useSelector((state: AppState) => state.auth.loggedInUser.personId);
  const businessUnitManagerSettings = useSelector((state: AppState) => state.auth.businessUnitManagerSettings);
  const [assistants, setAssistants] = React.useState<AutocompleteListItem[]>([]);
  const [defaultValues, setDefaultValues] = React.useState<SettingsFormTypes.EmployeeSettings>();
  const { canView, canManageEmployeeSalary, canManageSalarySettings } = useEmployeeHelpers();
  const { isWintSalarySettingsAvailable } = useSalaryPermissions();
  // From the defaults, not the employee prop: a save refreshes the defaults but not the prop, and after a
  // re-hire the saved employment is the new, open-ended one.
  const savedStartDate = defaultValues
    ? defaultValues.employmentStartDate
    : employee?.salarySettings?.employmentStartDate;
  const savedEndDate = defaultValues ? defaultValues.employmentEndDate : employee?.salarySettings?.employmentEndDate;
  const canRehire = isWintSalarySettingsAvailable && !!savedStartDate && isEmploymentEnded(savedEndDate);
  const isRehire = (startDate?: Date) =>
    canRehire && !isSameDay(startDate, savedStartDate) && isRehireStartDate(startDate, savedEndDate);
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const { getDialog: getScheduleChangeDialog } = useScheduleChangeDialog();
  const getReApproveDialog = useSalaryReApproveDialog();
  const [hasIncomingInvoicesSignee, setHasIncomingInvoicesSignee] = React.useState<boolean | undefined>();
  const [hasReceiptsAdmin, setHasReceiptsAdmin] = React.useState<boolean | undefined>();
  const selectedCompanyAddress = useSelector((state: AppState) => state.auth.selectedCompany.visitorAddress);
  const includeCostCenterDimensionToSalary = useSelector(
    (state: AppState) => state.auth.selectedCompany.includeCostCenterDimensionToSalary
  );

  const formRef = React.useRef<FormHandles>(null);
  React.useImperativeHandle(ref, () => (formRef?.current ? formRef.current : ({} as FormHandles)));

  // Track schedule view mode as a ref (not Formik state) to avoid dirty detection
  const scheduleViewModeRef = React.useRef<"current" | "scheduled">("current");

  const isDefaultBusinessUnitRequired =
    !employee?.id && !props.isAdmin && !props.isSalaryAdmin && props.isBusinessUnitManagerWithSalaryRights;

  const salaryAvailable = employee?.id ? canManageEmployeeSalary(employee.id) : canManageSalarySettings;

  // Shared equation test for the schedule/futureSchedule grids: skipped for the legacy flow and
  // empty grids, otherwise the grid must match its hours × rate sibling fields.
  const makeScheduleWorkTimeTest =
    (
      hoursField: "usualWorkHours" | "futureUsualWorkHours",
      rateField: "employmentRate" | "futureEmploymentRate"
    ) =>
    function (this: Yup.TestContext, value?: ScheduleWeekHours[]) {
      if (!isWintSalarySettingsAvailable || !Array.isArray(value) || value.length === 0) return true;
      const parent = this.parent as SettingsFormTypes.EmployeeSettings;
      return scheduleMatchesExpectedWorkTime(value, parent[hoursField], parent[rateField]);
    };

  const superkollSalaryType = useSelector((state: AppState) => state.auth.selectedCompany.superkollSalaryType);
  const createSalaryAutoFromSalaryPersonEntry = useSelector(
    (state: AppState) => state.auth.selectedCompany.createSalaryAutoFromSalaryPersonEntry
  );

  const companyUsesSimpleSalaryAuto =
    superkollSalaryType === WintSuperkollAPIModel.SuperkollSalaryType.Simple && createSalaryAutoFromSalaryPersonEntry;

  const isCreate = !employee?.id;
  const isEmployee = isCreate || (employee?.permissions?.employee ?? false);

  const validateForUpcomingEmployees = <T extends Yup.Schema>(
    schema: T,
    field: keyof SettingsFormTypes.EmployeeSettings
  ) =>
    schema.when([FormFieldConstants.Settings.EmployeeSettings.SalaryActive], ([salaryActive], schema: T) =>
      (salaryActive && defaultValues?.salaryActive && employee?.id && UtilsHelper.isEmpty(defaultValues[field])) ||
      !salaryActive
        ? schema
        : schema.required(TranslationKeys.Validations.Required)
    );

  // Helper function to check if a field is required based on bitmask
  const isFieldRequired = (
    fieldBit: WintSuperkollAPIModelWintSalary.WintSalaryOptionallyRequiredFields,
    wintSalaryType?: string
  ) => {
    if (!isWintSalarySettingsAvailable || !wintSalaryType) return false;
    const selectedType = availableSalaryTypes.find((t) => t.name === wintSalaryType);
    if (!selectedType) return false;
    return (selectedType.optionallyRequiredFields & fieldBit) !== 0;
  };

  // Helper function to determine default vacation conditions value
  const getDefaultVacationConditions = (employmentStartDate?: Date, wintSalaryType?: string): number | undefined => {
    if (!isWintSalarySettingsAvailable) {
      // Non-Wint systems: no default (keep null/undefined)
      return undefined;
    }
    if (!employmentStartDate || !wintSalaryType) {
      // Wint system but missing required fields: default to 0
      return 0;
    }
    if (!isFieldRequired(VACATION_DAYS_BIT, wintSalaryType)) {
      // Wint system but salary type doesn't require vacation days: default to 0
      return 0;
    }
    // Wint system with salary type that requires vacation days: default to 25
    return 25;
  };
  // Helper function to determine default salary change date
  const getDefaultSalaryChangeDate = (employmentStartDate?: Date): Date | undefined => {
    const today = DateHelper.today();
    // Create first of current month directly to avoid mutation issues
    const firstOfCurrentMonth = DateHelper.resetTime(new Date(today.getFullYear(), today.getMonth(), 1));

    if (!employmentStartDate || !firstOfCurrentMonth) {
      return firstOfCurrentMonth;
    }

    // Reset time for proper date-only comparison
    const employmentStart = DateHelper.resetTime(employmentStartDate);

    if (!employmentStart) {
      return firstOfCurrentMonth;
    }

    // Use the later date: first of current month, or employment start date if that is later
    return employmentStart.getTime() > firstOfCurrentMonth.getTime() ? employmentStart : firstOfCurrentMonth;
  };

  const validationSchema = React.useMemo(
    () =>
      Yup.object<EmployeeSettingsWithTemp>({
        name: Yup.string().when(FormFieldConstants.Settings.EmployeeSettings.Id, ([id], schema: Yup.StringSchema) =>
          !id
            ? schema
                .matches(RegularExpressions.FullName, {
                  message: TranslationKeys.Validations.InvalidFullName,
                  excludeEmptyString: true,
                })
                .required(TranslationKeys.Validations.Required)
            : schema
        ),
        personNumber: Yup.string()
          .matches(RegularExpressions.PersonNumber, {
            message: TranslationKeys.Validations.InvalidPersonNumber,
            excludeEmptyString: true,
          })
          .when(
            FormFieldConstants.Settings.EmployeeSettings.SalaryActive,
            ([salaryActive], schema: Yup.StringSchema) =>
              salaryActive && companyUsesSimpleSalaryAuto
                ? schema.required(TranslationKeys.Validations.Required)
                : schema
          )
          .when(
            FormFieldConstants.Settings.EmployeeSettings.EmploymentStartDate,
            ([employmentStartDate], schema: Yup.StringSchema) =>
              isWintSalarySettingsAvailable && employmentStartDate
                ? schema.required(TranslationKeys.Validations.Required)
                : schema
          ),
        personNumberFormatted: Yup.string(),
        incomingInvoiceReferenceNames: Yup.array().of(
          Yup.object<WintSuperkollAPIModel.IncomingInvoiceReferenceName>({
            id: Yup.number(),
            name: Yup.string(),
          })
        ),
        mail: Yup.string().email().required(TranslationKeys.Validations.Required),
        phone: Yup.string().validatePhone(TranslationKeys.Validations.InvalidPhone),
        employeeImage: Yup.array()
          .max(1, TranslationKeys.Settings.OneProfileImage)
          .of(
            Yup.object<UploadedFile>({
              data: Yup.string(),
              contentType: Yup.string(),
              fileName: Yup.string(),
              fileSize: Yup.number(),
            })
          ),
        cEO: Yup.boolean(),
        employee: Yup.boolean(),
        invoices: Yup.boolean(),
        invoicesAdmin: Yup.boolean(),
        incomingInvoices: Yup.boolean(),
        incomingInvoicesAdmin: Yup.boolean(),
        incomingInvoicesOverview: Yup.boolean(),
        incomingInvoicesSignee: Yup.boolean(),
        paymentFileSignee: Yup.boolean(),
        receipts: Yup.boolean(),
        receiptsAdmin: Yup.boolean(),
        reports: Yup.boolean(),
        missingDocumentAdmin: Yup.boolean(),
        documents: Yup.boolean(),
        uploadGenericDocuments: Yup.boolean(),
        agreement: Yup.boolean(),
        auditor: Yup.boolean(),
        revisor: Yup.boolean(),
        salaryAdmin: Yup.boolean(),
        deviationAdmin: Yup.boolean(),
        paymentSignee: Yup.boolean(),
        stoppedPaymentAdmin: Yup.boolean(),
        admin: Yup.boolean(),
        contact: Yup.boolean(),
        board: Yup.boolean(),
        cashReconciliation: Yup.boolean(),
        bankAccount: Yup.string()
          .matches(/^[0-9]{7,10}$/, {
            message: TranslationKeys.Validations.InvalidBankAccount,
            excludeEmptyString: true,
          })
          .when(
            [
              FormFieldConstants.Settings.EmployeeSettings.SalaryActive,
              FormFieldConstants.Settings.EmployeeSettings.EmploymentStartDate,
            ],
            ([salaryActive, employmentStartDate], schema: Yup.StringSchema) =>
              salaryActive || (isWintSalarySettingsAvailable && employmentStartDate)
                ? schema.required(TranslationKeys.Validations.Required)
                : schema
          ),
        bankAccountClearing: Yup.string()
          .matches(/^[0-9]{4,5}$/, {
            message: TranslationKeys.Validations.InvalidClearingNumber,
            excludeEmptyString: true,
          })
          .when(
            [
              FormFieldConstants.Settings.EmployeeSettings.SalaryActive,
              FormFieldConstants.Settings.EmployeeSettings.EmploymentStartDate,
            ],
            ([salaryActive, employmentStartDate], schema: Yup.StringSchema) =>
              salaryActive || (isWintSalarySettingsAvailable && employmentStartDate)
                ? schema.required(TranslationKeys.Validations.Required)
                : schema
          ),
        taxTable: Yup.number(),
        taxTableName: Yup.string().when(
          FormFieldConstants.Settings.EmployeeSettings.SalaryActive,
          ([salaryActive], schema: Yup.StringSchema) =>
            salaryActive ? schema.required(TranslationKeys.Validations.Required) : schema
        ),
        employmentStartDate: Yup.date()
          .typeError(TranslationKeys.Validations.InvalidDate)
          .test("cannot-remove-if-set", TranslationKeys.Validations.Required, function (value) {
            if (!isWintSalarySettingsAvailable) {
              return true;
            }

            // If there was an original start date, the field cannot be cleared when using WintSalary
            const originalStartDate = employee?.salarySettings?.employmentStartDate;
            if (originalStartDate && !value) {
              return false;
            }

            return true;
          })
          .test(
            "rehire-start-after-end-date",
            TranslationKeys.Validations.RehireStartDateMustBeAfterEndDate,
            (value) =>
              !canRehire || !value || isSameDay(value, savedStartDate) || isRehireStartDate(value, savedEndDate)
          ),
        employmentEndDate: Yup.date().typeError(TranslationKeys.Validations.InvalidDate),
        terminationReason: Yup.mixed<WintSuperkollAPIModel.EmploymentTerminationReason>(),
        terminationReasonName: Yup.string().when(
          [
            FormFieldConstants.Settings.EmployeeSettings.EmploymentEndDate,
            FormFieldConstants.Settings.EmployeeSettings.TerminationReason,
            FormFieldConstants.Settings.EmployeeSettings.CanChangeTerminationReason,
          ],
          ([employmentEndDate, terminationReason, canChangeTerminationReason], schema: Yup.StringSchema) => {
            const hasReason =
              terminationReason != null &&
              terminationReason !== WintSuperkollAPIModel.EmploymentTerminationReason.Undefined;
            return salaryAvailable &&
              employmentEndDate &&
              isWintSalarySettingsAvailable &&
              canChangeTerminationReason !== false &&
              !hasReason
              ? schema.required(TranslationKeys.Validations.Required)
              : schema;
          }
        ),
        monthlySalary: Yup.number().when(
          [
            FormFieldConstants.Settings.EmployeeSettings.SalaryActivated,
            FormFieldConstants.Settings.EmployeeSettings.EmploymentStartDate,
            FormFieldConstants.Settings.EmployeeSettings.WintSalaryType,
          ],
          ([salaryActivated, employmentStartDate, wintSalaryType], schema: Yup.NumberSchema) => {
            // Legacy system - require if salary is activated or employment start date is set
            if (!isWintSalarySettingsAvailable) {
              return salaryActivated || employmentStartDate
                ? schema.required(TranslationKeys.Validations.Required)
                : schema;
            }

            // WintSalary system - require if monthly gross salary flag is set
            if (wintSalaryType && isFieldRequired(MONTHLY_GROSS_SALARY_BIT, wintSalaryType)) {
              return schema
                .required(TranslationKeys.Validations.Required)
                .min(
                  0,
                  LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Min, "", { min: "0" })
                );
            }

            return schema;
          }
        ),
        hourlySalary: Yup.number().when(
          FormFieldConstants.Settings.EmployeeSettings.WintSalaryType,
          ([wintSalaryType], schema: Yup.NumberSchema) => {
            if (!isWintSalarySettingsAvailable || !wintSalaryType) return schema;
            return isFieldRequired(HOURLY_GROSS_SALARY_BIT, wintSalaryType)
              ? schema
                  .required(TranslationKeys.Validations.Required)
                  .min(
                    0,
                    LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Min, "", { min: "0" })
                  )
              : schema;
          }
        ),
        wintSalaryType: Yup.string().when(
          FormFieldConstants.Settings.EmployeeSettings.EmploymentStartDate,
          ([employmentStartDate], schema: Yup.StringSchema) =>
            isWintSalarySettingsAvailable && employmentStartDate
              ? schema.required(TranslationKeys.Validations.Required)
              : schema
        ),
        employmentRate: Yup.number()
          .min(0, TranslationKeys.Settings.EmploymentRateValidation)
          .max(100, TranslationKeys.Settings.EmploymentRateValidation)
          .required(TranslationKeys.Validations.Required),
        additionalBankAccount: Yup.string().matches(/^[0-9]{7,10}$/, {
          message: TranslationKeys.Validations.InvalidBankAccount,
          excludeEmptyString: true,
        }),
        additionalBankAccountClearing: Yup.string().matches(/^[0-9]{4,5}$/, {
          message: TranslationKeys.Validations.InvalidClearingNumber,
          excludeEmptyString: true,
        }),
        assistants: Yup.array().of(
          Yup.object<WintSuperkollAPIModel.EmployeeChildAssistant>({
            id: Yup.number(),
            name: Yup.string(),
          })
        ),
        quickSelection: Yup.number(),
        salaryActive: Yup.boolean(),
        salaryChangeFrom: Yup.date()
          .when(
            [FormFieldConstants.Settings.EmployeeSettings.MonthlySalary, "hourlySalary"],
            ([monthlySalary, hourlySalary], schema: Yup.DateSchema) => {
              const defaultMonthlySalary = defaultValues?.monthlySalary;
              const defaultHourlySalary = defaultValues?.hourlySalary;
              const monthlySalaryChanged = monthlySalary && monthlySalary !== defaultMonthlySalary;
              const hourlySalaryChanged = hourlySalary && hourlySalary !== defaultHourlySalary;
              const anySalaryChanged = monthlySalaryChanged || hourlySalaryChanged;

              return anySalaryChanged && !!employee?.id
                ? schema
                    .required(TranslationKeys.Validations.Required)
                    .typeError(TranslationKeys.Validations.InvalidDate)
                : schema;
            }
          )
          .when(
            FormFieldConstants.Settings.EmployeeSettings.EmploymentStartDate,
            ([employmentStartDate], schema: Yup.DateSchema) =>
              employmentStartDate && DateHelper.isValidDate(employmentStartDate)
                ? schema.min(
                    employmentStartDate,
                    TranslationKeys.Validations.ChangeSalaryDateMustBeLaterThanEmploymentDate
                  )
                : schema
          ),
        workplaceStreetAddress: Yup.string().max(50),
        workplaceCity: Yup.string().max(50),
        employmentRateChangeFrom: Yup.date().when(
          FormFieldConstants.Settings.EmployeeSettings.EmploymentRate,
          ([employmentRate], schema: Yup.DateSchema) =>
            employmentRate &&
            employmentRate !== employee?.salarySettings?.employmentRate &&
            !!employee?.id &&
            !isWintSalarySettingsAvailable
              ? schema.required(TranslationKeys.Validations.Required).typeError(TranslationKeys.Validations.InvalidDate)
              : schema
        ),
        isTimeReportingMandatory: Yup.boolean(),
        timeReportingInterval: Yup.number(),
        timeReportingIntervalName: Yup.string().required(TranslationKeys.Validations.Required),
        businessUnitName: Yup.string().when(
          FormFieldConstants.Settings.EmployeeSettings.SalaryActive,
          ([salaryActive], schema: Yup.StringSchema) =>
            !employee?.id &&
            !props.isSalaryAdmin &&
            props.isBusinessUnitManagerWithSalaryRights &&
            (!props.isAdmin || salaryActive)
              ? schema.required(TranslationKeys.Validations.Required)
              : schema
        ),
        businessUnitId: Yup.string(),
        position: validateForUpcomingEmployees(
          Yup.string().trim(),
          FormFieldConstants.Settings.EmployeeSettings.Position
        ),
        vacationConditions: validateForUpcomingEmployees(
          Yup.number()
            .max(
              100,
              LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Max, "", { max: "100" })
            )
            .when(
              FormFieldConstants.Settings.EmployeeSettings.WintSalaryType,
              ([wintSalaryType], schema: Yup.NumberSchema) =>
                isFieldRequired(VACATION_DAYS_BIT, wintSalaryType)
                  ? schema.required(TranslationKeys.Validations.Required)
                  : schema
            ),
          FormFieldConstants.Settings.EmployeeSettings.VacationConditions
        ),
        occupationalCategorisationId: validateForUpcomingEmployees(
          Yup.number(),
          FormFieldConstants.Settings.EmployeeSettings.OccupationalCategorisationId
        ),
        occupationalCategorisation: validateForUpcomingEmployees(
          Yup.string(),
          FormFieldConstants.Settings.EmployeeSettings.OccupationalCategorisation
        ),
        usualWorkHours: validateForUpcomingEmployees(
          Yup.number().max(
            40,
            LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Max, "", { max: "40" })
          ),
          FormFieldConstants.Settings.EmployeeSettings.UsualWorkHours
        ),
        scheduleDescription: Yup.string()
          .trim()
          .when(
            [
              FormFieldConstants.Settings.EmployeeSettings.EmploymentRate,
              FormFieldConstants.Settings.EmployeeSettings.SalaryActive,
            ],
            ([emplyementRate, salaryActivate], schema: Yup.StringSchema) =>
              !isWintSalarySettingsAvailable &&
              salaryActivate &&
              (!employee?.id ||
                !defaultValues?.salaryActive ||
                (employee?.id && defaultValues?.employmentRate !== emplyementRate)) &&
              emplyementRate < 100
                ? schema.required(TranslationKeys.Validations.Required)
                : schema
          ),
        tempVacationCompensation: Yup.number().when(
          FormFieldConstants.Settings.EmployeeSettings.WintSalaryType,
          ([wintSalaryType], schema: Yup.NumberSchema) => {
            const isHourly = isFieldRequired(HOURLY_GROSS_SALARY_BIT, wintSalaryType);
            const minValue = isHourly ? 12 : 0.14;
            const minLabel = isHourly ? "12" : "0.14";

            let validatedSchema = schema
              .min(
                minValue,
                LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Min, "", { min: minLabel })
              )
              .max(
                15,
                LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Max, "", { max: "15" })
              );

            if (isFieldRequired(VACATION_COMPENSATION_BIT, wintSalaryType)) {
              validatedSchema = validatedSchema.required(TranslationKeys.Validations.Required);
            }

            return validatedSchema;
          }
        ),
        schedule: Yup.array()
          .when(
            FormFieldConstants.Settings.EmployeeSettings.WintSalaryType,
            ([wintSalaryType]: [string | undefined], schema: any) =>
              isFieldRequired(SCHEDULE_BIT, wintSalaryType)
                ? schema.min(1, TranslationKeys.Validations.Required).required(TranslationKeys.Validations.Required)
                : schema
          )
          .test(
            "schedule-matches-expected-work-time",
            TranslationKeys.Settings.ScheduleWorkTimeMismatch,
            makeScheduleWorkTimeTest("usualWorkHours", "employmentRate")
          ),
        futureSchedule: Yup.array().test(
          "schedule-matches-expected-work-time",
          TranslationKeys.Settings.ScheduleWorkTimeMismatch,
          makeScheduleWorkTimeTest("futureUsualWorkHours", "futureEmploymentRate")
        ),
        futureEmploymentRate: Yup.number()
          .min(0, TranslationKeys.Settings.EmploymentRateValidation)
          .max(100, TranslationKeys.Settings.EmploymentRateValidation),
        futureUsualWorkHours: Yup.number().max(
          40,
          LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Max, "", { max: "40" })
        ),
        // scheduleViewMode removed - not a Formik field
      }),
    [
      defaultValues,
      employee,
      isWintSalarySettingsAvailable,
      salaryAvailable,
      availableSalaryTypes,
      companyUsesSimpleSalaryAuto,
      canRehire,
      savedStartDate,
      savedEndDate,
    ]
  );

  const allPermissions = [
    "cEO",
    "contact",
    "employee",
    "documents",
    "incomingInvoicesOverview",
    "incomingInvoices",
    "invoicesAdmin",
    "invoices",
    "receiptsAdmin",
    "receipts",
    "admin",
    "reports",
    "incomingInvoicesAdmin",
    "incomingInvoicesSignee",
    "agreement",
    "cashReconciliation",
    "paymentFileSignee",
    "missingDocumentAdmin",
    "auditor",
    "revisor",
    "salaryAdmin",
    "deviationAdmin",
    "paymentSignee",
    "stoppedPaymentAdmin",
  ];

  const employeePermissions = [
    FormFieldConstants.Settings.EmployeeSettings.Employee,
    FormFieldConstants.Settings.EmployeeSettings.IncomingInvoices,
    FormFieldConstants.Settings.EmployeeSettings.Invoices,
    FormFieldConstants.Settings.EmployeeSettings.Receipts,
  ] as string[];

  const salaryAdminPermissions = [
    FormFieldConstants.Settings.EmployeeSettings.Employee,
    FormFieldConstants.Settings.EmployeeSettings.IncomingInvoices,
    FormFieldConstants.Settings.EmployeeSettings.Invoices,
    FormFieldConstants.Settings.EmployeeSettings.Receipts,
    FormFieldConstants.Settings.EmployeeSettings.SalaryAdmin,
  ] as string[];

  const inactiveForCFO = [
    FormFieldConstants.Settings.EmployeeSettings.Admin,
    FormFieldConstants.Settings.EmployeeSettings.Auditor,
    FormFieldConstants.Settings.EmployeeSettings.CEO,
  ] as string[];

  const cFOPermissions = allPermissions && allPermissions.filter((p) => !inactiveForCFO.includes(p));
  const activatedPermissions =
    employee &&
    employee.permissions &&
    Object.entries(employee.permissions)
      .filter(([item, val]) => val === true)
      .map(([item, val]) => item);

  const permissionComparator = (quickSelectionPermissions: string[]) => {
    return (
      // eslint-disable-next-line eqeqeq --- disabling to avoid regression testing if changed
      activatedPermissions?.length == quickSelectionPermissions.length &&
      activatedPermissions.every((a) => quickSelectionPermissions.includes(a))
    );
  };

  const getDefaultQuickSelection = () => {
    if (permissionComparator(employeePermissions)) {
      return EmployeeType.Employee;
    } else if (permissionComparator(salaryAdminPermissions)) {
      return EmployeeType.SalaryAdmin;
    } else if (cFOPermissions && permissionComparator(cFOPermissions)) {
      return EmployeeType.CFO;
    }
  };

  const timeReportingInterval =
    employee?.timeReportingSettings?.timeReportingInterval ?? WintSuperkollAPIModel.TimeReportingInterval.Weekly;

  const timeReportingIntervalName = LocalizationHelperInstance.getTranslatedEnumValue(
    TranslationKeys.Settings.TimeReportingInterval,
    timeReportingInterval
  );

  const getDefaultValues = (employee?: WintSuperkollAPIModel.ExtendedEmployee): EmployeeSettingsWithTemp => {
    const personNumber = employee?.pno ? PersonNumberHelper.clearOtherCharacters(employee?.pno) : undefined;

    return {
      name: employee?.name,
      personNumberFormatted: personNumber
        ? UtilsHelper.applyMask(personNumber, CommonConstants.PersonNumberMask)
        : personNumber,
      personNumber,
      incomingInvoiceReferenceNames: employee?.incomingInvoiceReferenceNames,
      phone: employee?.phone,
      mail: employee?.mail,
      employeeImage: [
        {
          data: employee?.profileImage?.imageData,
          contentType: employee?.profileImage?.contentType,
        } as UploadedFile,
      ],
      invoices: employee?.permissions?.invoices,
      invoicesAdmin: employee?.permissions?.invoicesAdmin,
      incomingInvoices: employee?.permissions?.incomingInvoices,
      incomingInvoicesOverview: employee?.permissions?.incomingInvoicesOverview,
      incomingInvoicesSignee: employee?.permissions?.incomingInvoicesSignee,
      incomingInvoicesAdmin: employee?.permissions?.incomingInvoicesAdmin,
      paymentFileSignee: employee?.permissions?.paymentFileSignee,
      receipts: employee?.permissions?.receipts,
      receiptsAdmin: employee?.permissions?.receiptsAdmin,
      reports: employee?.permissions?.reports,
      missingDocumentAdmin: employee?.permissions?.missingDocumentAdmin,
      documents: employee?.permissions?.documents,
      uploadGenericDocuments: employee?.permissions?.uploadGenericDocuments,
      agreement: employee?.permissions?.agreement,
      auditor: employee?.permissions?.auditor,
      revisor: employee?.permissions?.revisor,
      deviationAdmin: employee?.permissions?.deviationAdmin,
      salaryAdmin: employee?.permissions?.salaryAdmin,
      paymentSignee: employee?.permissions?.paymentSignee,
      stoppedPaymentAdmin: employee?.permissions?.stoppedPaymentAdmin,
      admin: employee?.permissions?.admin,
      contact: employee?.permissions?.contact,
      board: employee?.permissions?.board,
      cashReconciliation: employee?.permissions?.cashReconciliation,
      bankAccountClearing: employee?.salarySettings?.bankAccountClearing,
      taxTable: employee?.salarySettings?.taxTable,
      taxTableName:
        // eslint-disable-next-line eqeqeq --- disabling to avoid regression testing if changed
        employee?.salarySettings?.taxTable == WintSuperkollAPIModel.TaxTableInfo.None
          ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.None)
          : employee?.salarySettings?.taxTable?.toString(),
      taxAdjustment: employee?.salarySettings?.wintSalarySpecificSettings?.taxPercentage
        ? employee.salarySettings.wintSalarySpecificSettings.taxPercentage * 100
        : undefined,
      bankAccount: employee?.salarySettings?.bankAccount,
      employmentStartDate: employee?.salarySettings?.employmentStartDate,
      employmentEndDate: employee?.salarySettings?.employmentEndDate,
      terminationReason: employee?.salarySettings?.terminationReason,
      terminationReasonName: getTerminationReasonName(employee?.salarySettings?.terminationReason),
      canChangeTerminationReason: employee?.salarySettings?.canChangeTerminationReason,
      monthlySalary: employee?.salarySettings?.monthlySalary,
      hourlySalary: employee?.salarySettings?.wintSalarySpecificSettings?.hourlySalary,
      employmentRate: isWintSalarySettingsAvailable
        ? (employee?.salarySettings?.wintSalarySpecificSettings?.schedule?.currentSchedule?.workRate ??
            employee?.salarySettings?.wintSalarySpecificSettings?.schedule?.futureSchedule?.workRate ??
            1.0) * 100
        : employee?.salarySettings?.employmentRate,
      additionalBankAccount: employee?.additionalBankAccount,
      additionalBankAccountClearing: employee?.additionalBankAccountClearing,
      assistants: employee?.assistants,
      employee: employee?.permissions?.employee,
      cEO: employee?.permissions?.cEO,
      // Clear salaryChangeFrom on load - only populate when field is shown (when salary changes)
      salaryChangeFrom: undefined,
      workplaceStreetAddress: employee?.salarySettings?.workPlaceStreetAddress ?? selectedCompanyAddress?.street1,
      workplaceCity: employee?.salarySettings?.workPlaceCity ?? selectedCompanyAddress?.city,
      employmentRateChangeFrom: employee?.salarySettings?.employmentRateChangeFrom,
      quickSelection: getDefaultQuickSelection(),
      salaryActive: employee?.salarySettings?.isActive,
      salaryActivated: false,
      isTimeReportingMandatory: employee?.timeReportingSettings?.isTimeReportingMandatory,
      timeReportingInterval: timeReportingInterval,
      timeReportingIntervalName: timeReportingIntervalName,
      advisor: employee?.permissions?.advisor,
      position: employee?.salarySettings?.position,
      vacationConditions:
        employee?.salarySettings?.vacationConditions ??
        getDefaultVacationConditions(
          employee?.salarySettings?.employmentStartDate,
          employee?.salarySettings?.wintSalarySpecificSettings?.selectedSalaryType?.name
        ),
      // Map nested field to temporary field for form validation (convert decimal to percentage for display)
      tempVacationCompensation: employee?.salarySettings?.wintSalarySpecificSettings?.vacationCompensation
        ? employee.salarySettings.wintSalarySpecificSettings.vacationCompensation * 100
        : undefined,
      wintSalarySpecificSettings: employee?.salarySettings?.wintSalarySpecificSettings,
      // Map selectedSalaryType.name to wintSalaryType string for form field
      wintSalaryType: employee?.salarySettings?.wintSalarySpecificSettings?.selectedSalaryType?.name,

      // If no schedule exists, provide a default one-week schedule
      schedule:
        employee?.salarySettings?.wintSalarySpecificSettings?.schedule?.currentSchedule?.scheduleWeeks?.map(
          convertScheduleWeekToHours
        ) ??
        (isWintSalarySettingsAvailable
          ? [
              generateDefaultScheduleWeek(
                (employee?.salarySettings?.wintSalarySpecificSettings?.schedule?.currentSchedule?.standardWorkTime ??
                  employee?.salarySettings?.wintSalarySpecificSettings?.schedule?.futureSchedule?.standardWorkTime ??
                  2400) / 60,
                (employee?.salarySettings?.wintSalarySpecificSettings?.schedule?.currentSchedule?.workRate ??
                  employee?.salarySettings?.wintSalarySpecificSettings?.schedule?.futureSchedule?.workRate ??
                  1.0) * 100
              ),
            ]
          : undefined),

      // Future schedule fields - only populated if there's a future schedule
      futureSchedule:
        employee?.salarySettings?.wintSalarySpecificSettings?.schedule?.futureSchedule?.scheduleWeeks?.map(
          convertScheduleWeekToHours
        ),
      futureEmploymentRate: employee?.salarySettings?.wintSalarySpecificSettings?.schedule?.futureSchedule?.workRate
        ? employee.salarySettings.wintSalarySpecificSettings.schedule.futureSchedule.workRate * 100
        : undefined,
      futureUsualWorkHours: employee?.salarySettings?.wintSalarySpecificSettings?.schedule?.futureSchedule
        ?.standardWorkTime
        ? employee.salarySettings.wintSalarySpecificSettings.schedule.futureSchedule.standardWorkTime / 60
        : undefined,
      // scheduleViewMode removed - managed as React state, not Formik field
      occupationalCategorisationId: employee?.salarySettings?.workType,
      occupationalCategorisation:
        employee?.salarySettings?.workType !== undefined
          ? LocalizationHelperInstance.getTranslatedEnumValue(
              TranslationKeys.Settings.OccupationalCategorisation,
              employee?.salarySettings?.workType
            )
          : "",

      usualWorkHours: isWintSalarySettingsAvailable
        ? (employee?.salarySettings?.wintSalarySpecificSettings?.schedule?.currentSchedule?.standardWorkTime ??
            employee?.salarySettings?.wintSalarySpecificSettings?.schedule?.futureSchedule?.standardWorkTime ??
            2400) / 60
        : (employee?.salarySettings?.usualWorkHours ?? 40),
      scheduleDescription: employee?.salarySettings?.schedule,
      salaryCostCenter: employee?.salarySettings?.costCenter ? [employee.salarySettings.costCenter] : [],
      userTypeId: employee?.userTypeId,
    };
  };

  const updatePermissions = (
    formikProps: FormikProps<SettingsFormTypes.EmployeeSettings>,
    activePermissions: string[]
  ) => {
    if (!activePermissions) return;
    const { setValues, values } = formikProps;
    const newValues = { ...values } as any;
    allPermissions?.forEach((p) => {
      newValues[p] = activePermissions.includes(p) ? true : false;
    });
    setValues(newValues);
  };

  const handleQuickSelection = (
    name: string,
    updatedValue: EmployeeType,
    formikProps: FormikProps<SettingsFormTypes.EmployeeSettings>
  ) => {
    switch (updatedValue) {
      case EmployeeType.Employee: {
        updatePermissions(formikProps, employeePermissions);
        break;
      }
      case EmployeeType.SalaryAdmin: {
        updatePermissions(formikProps, salaryAdminPermissions);
        break;
      }
      case EmployeeType.CFO: {
        cFOPermissions && updatePermissions(formikProps, cFOPermissions);
        break;
      }
      default: {
        updatePermissions(formikProps, activatedPermissions as string[]);
        break;
      }
    }
  };

  const activatingIncomingInvoicesSigneeDialog: (onCancel: () => void) => DialogProps = (onCancel: () => void) => ({
    isOpen: false,
    title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.AreYouSure),
    titleIcon: IconDefinitionConstants.WQuestionCircle,
    dialogType: DialogEnums.DialogType.Message,
    children: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.IncomingInvoicesSigneeWarning),
    maxWidth: "sm",
    hideCancelButton: true,
    hideCloseButton: true,
    actions: [
      {
        text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Yes),
        type: DisplayType.Success,
        onClick: () => Promise.resolve(),
      },
      {
        text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.No),
        type: DisplayType.Danger,
        onClick: () => {
          onCancel();
          return Promise.resolve();
        },
      },
    ],
  });

  const activatingReceiptsAdminDialog: (onCancel: () => void) => DialogProps = (onCancel: () => void) => ({
    isOpen: false,
    title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.AreYouSure),
    titleIcon: IconDefinitionConstants.WQuestionCircle,

    dialogType: DialogEnums.DialogType.Message,
    children: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.ReceiptsAdminWarning),
    maxWidth: "sm",
    hideCancelButton: true,
    hideCloseButton: true,
    actions: [
      {
        text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Yes),
        type: DisplayType.Success,
        onClick: () => Promise.resolve(),
      },
      {
        text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.No),
        type: DisplayType.Danger,
        onClick: () => {
          onCancel();
          return Promise.resolve();
        },
      },
    ],
  });

  const onPermissionChanged = (
    name: string,
    updatedValue: any,
    formikProps: FormikProps<SettingsFormTypes.EmployeeSettings>,
    userInput?: boolean
  ) => {
    const { setFieldValue, values } = formikProps;
    if (
      !hasIncomingInvoicesSignee &&
      name === FormFieldConstants.Settings.EmployeeSettings.IncomingInvoicesSignee &&
      updatedValue === true
    ) {
      dispatch(
        displayDialog(
          activatingIncomingInvoicesSigneeDialog(() => {
            setFieldValue(FormFieldConstants.Settings.EmployeeSettings.IncomingInvoicesSignee, false);
          })
        )
      );
    }
    if (
      !hasReceiptsAdmin &&
      name === FormFieldConstants.Settings.EmployeeSettings.ReceiptsAdmin &&
      updatedValue === true
    ) {
      dispatch(
        displayDialog(
          activatingReceiptsAdminDialog(() => {
            setFieldValue(FormFieldConstants.Settings.EmployeeSettings.ReceiptsAdmin, false);
          })
        )
      );
    }
    if (userInput && values.quickSelection) {
      setFieldValue(FormFieldConstants.Settings.EmployeeSettings.QuickSelection, undefined);
    }
  };

  const onSalaryChange = (
    name: string,
    updatedValue: any,
    formikProps: FormikProps<SettingsFormTypes.EmployeeSettings>
  ) => {
    const { setFieldValue, values } = formikProps;

    // Check if salary has changed from default
    const monthlySalaryChanged =
      !UtilsHelper.isEmpty(values.monthlySalary) && values.monthlySalary !== defaultValues?.monthlySalary;
    const hourlySalaryChanged =
      !UtilsHelper.isEmpty(values.hourlySalary) && values.hourlySalary !== defaultValues?.hourlySalary;
    const anySalaryChanged = monthlySalaryChanged || hourlySalaryChanged;

    // When salary changes set the default change date
    if (anySalaryChanged) {
      setFieldValue(
        FormFieldConstants.Settings.EmployeeSettings.SalaryChangeFrom,
        getDefaultSalaryChangeDate(values.employmentStartDate)
      );
    }
  };

  const onSalaryToggle = (
    name: string,
    updatedValue: any,
    formikProps: FormikProps<SettingsFormTypes.EmployeeSettings>
  ) => {
    const { setFieldValue, values } = formikProps;
    if (!values.salaryActive) {
      setFieldValue(
        FormFieldConstants.Settings.EmployeeSettings.MonthlySalary,
        employee?.salarySettings?.monthlySalary
      );

      // Determine if the salary was not initially active and is still not active
      if (!defaultValues?.salaryActive && values.salaryActivated) {
        setFieldValue(FormFieldConstants.Settings.EmployeeSettings.SalaryActivated, false);
      }
    } else {
      setFieldValue(
        FormFieldConstants.Settings.EmployeeSettings.SalaryChangeFrom,
        employee?.salarySettings?.salaryChangeFrom
      );

      // Determine if the salary was not initially active and is now active
      if (!defaultValues?.salaryActive && !values.salaryActivated) {
        setFieldValue(FormFieldConstants.Settings.EmployeeSettings.SalaryActivated, true);
      }
    }
  };

  const getBusinessUnitsWithSalaryRights = (searchStr?: string) => {
    return BusinessUnitService.searchBusinessUnits(searchStr).then((result) => {
      return result
        .filter((businessUnit) => businessUnit.managerId === loggedInUserId && businessUnit.managerHasSalaryRights)
        .map(
          (businessUnit): AutocompleteListItem => ({
            id: businessUnit.id,
            value: businessUnit.name ?? "",
            icon: IconDefinitionConstants.WCustomer,
          })
        );
    });
  };

  const saveEmployee = async (values: EmployeeSettingsWithTemp, effectiveDate?: Date) => {
    const employeePut: WintSuperkollAPIModel.EmployeePut = {
      name: values.name,
      pno: values.personNumberFormatted ?? "",
      phone: values.phone,
      incomingInvoiceReferenceNames: values.incomingInvoiceReferenceNames?.filter((name) => !UtilsHelper.isEmpty(name)),
      additionalBankAccount: values.additionalBankAccount,
      additionalBankAccountClearing: values.additionalBankAccountClearing,
      profileImage: employee?.profileImage, //Sending already contained prof image
      assistants: values.assistants?.filter((a) => a != null && a.id != null),
      userTypeId: values.userTypeId,
      permissions: {
        cEO: values.cEO ?? false,
        contact: values.contact ?? false,
        employee: values.employee ?? false,
        documents: values.documents ?? false,
        uploadGenericDocuments: values.uploadGenericDocuments ?? false,
        incomingInvoicesOverview: values.incomingInvoicesOverview ?? false,
        incomingInvoices: values.incomingInvoices ?? false,
        invoicesAdmin: values.invoicesAdmin ?? false,
        invoices: values.invoices ?? false,
        receiptsAdmin: values.receiptsAdmin ?? false,
        receipts: values.receipts ?? false,
        admin: values.admin ?? false,
        reports: values.reports ?? false,
        incomingInvoicesAdmin: values.incomingInvoicesAdmin ?? false,
        incomingInvoicesSignee: values.incomingInvoicesSignee ?? false,
        agreement: values.agreement ?? false,
        cashReconciliation: values.cashReconciliation ?? false,
        paymentFileSignee: values.paymentFileSignee ?? false,
        missingDocumentAdmin: values.missingDocumentAdmin ?? false,
        auditor: values.auditor ?? false,
        revisor: values.revisor ?? false,
        salaryAdmin: values.salaryAdmin ?? false,
        deviationAdmin: values.deviationAdmin ?? false,
        paymentSignee: values.paymentSignee ?? false,
        stoppedPaymentAdmin: values.stoppedPaymentAdmin ?? false,
        advisor: values.advisor ?? false,
        board: values.board ?? false,
      },
      timeReportingSettings: {
        timeReportingInterval: values.timeReportingInterval ?? WintSuperkollAPIModel.TimeReportingInterval.Weekly,
        isTimeReportingMandatory: values.isTimeReportingMandatory ?? false,
      },
    };

    let wintSalarySpecificSettings: any = undefined;
    const requiresMonthly = isFieldRequired(MONTHLY_GROSS_SALARY_BIT, values.wintSalaryType);
    const requiresHourly = isFieldRequired(HOURLY_GROSS_SALARY_BIT, values.wintSalaryType);

    if (isWintSalarySettingsAvailable) {
      // Determine which schedule data to use based on view mode (from ref, not form values)
      const isEditingFutureSchedule = scheduleViewModeRef.current === "scheduled";

      // Convert schedule weeks from hours to minutes
      const currentScheduleWeeks = values.schedule?.map(convertScheduleWeekToMinutes);

      const futureScheduleWeeks = values.futureSchedule?.map(convertScheduleWeekToMinutes);

      // A re-hire creates a new employment with a schedule of its own, and a pending change belonging to
      // the old employment is dropped rather than dated before the new one. Wint.Salary refuses a current
      // schedule that is not yet valid, so a re-hire registered ahead of time gets one from today, as a new
      // hire with a future start date does.
      const rehireScheduleValidFrom = isRehire(values.employmentStartDate)
        ? DateHelper.convertDateToShortISOString(
            new Date(Math.min(new Date(values.employmentStartDate!).getTime(), new Date().getTime()))
          )
        : undefined;
      const existingSchedule =
        values.wintSalarySpecificSettings?.schedule?.currentSchedule ??
        values.wintSalarySpecificSettings?.schedule?.futureSchedule;
      const rehireCurrentSchedule =
        currentScheduleWeeks && currentScheduleWeeks.length > 0
          ? {
              validFrom: rehireScheduleValidFrom,
              workRate: (values.employmentRate ?? 100) / 100,
              standardWorkTime: (values.usualWorkHours ?? 40) * 60,
              scheduleWeeks: currentScheduleWeeks,
            }
          : existingSchedule && { ...existingSchedule, validFrom: rehireScheduleValidFrom };

      // Find the selected salary type from available types to get correct name, description, and flags
      const selectedType = availableSalaryTypes.find((type) => type.name === values.wintSalaryType);

      wintSalarySpecificSettings = {
        selectedSalaryType: selectedType || values.wintSalarySpecificSettings?.selectedSalaryType,
        vacationCompensation: values.tempVacationCompensation
          ? values.tempVacationCompensation / 100 // Convert percentage to decimal
          : values.wintSalarySpecificSettings?.vacationCompensation,
        hourlySalary: requiresHourly ? values.hourlySalary : undefined,
        availableSalaryTypes: values.wintSalarySpecificSettings?.availableSalaryTypes,
        schedule: rehireScheduleValidFrom
          ? { currentSchedule: rehireCurrentSchedule, futureSchedule: null }
          : isEditingFutureSchedule
          ? // Editing future schedule - update futureSchedule, preserve currentSchedule from form fields
            {
              currentSchedule:
                currentScheduleWeeks && currentScheduleWeeks.length > 0
                  ? {
                      validFrom: values.wintSalarySpecificSettings?.schedule?.currentSchedule?.validFrom
                        ? DateHelper.convertDateToShortISOString(
                            new Date(values.wintSalarySpecificSettings.schedule.currentSchedule.validFrom as any)
                          )
                        : values.employmentStartDate
                          ? DateHelper.convertDateToShortISOString(new Date(values.employmentStartDate))
                          : DateHelper.convertDateToShortISOString(new Date()),
                      workRate: (values.employmentRate ?? 100) / 100,
                      standardWorkTime: (values.usualWorkHours ?? 40) * 60,
                      scheduleWeeks: currentScheduleWeeks,
                    }
                  : undefined,
              futureSchedule: futureScheduleWeeks
                ? {
                    validFrom: effectiveDate
                      ? DateHelper.convertDateToShortISOString(effectiveDate)
                      : DateHelper.convertDateToShortISOString(new Date()),
                    workRate: (values.futureEmploymentRate ?? 100) / 100,
                    standardWorkTime: (values.futureUsualWorkHours ?? 40) * 60,
                    scheduleWeeks: futureScheduleWeeks,
                  }
                : null,
            }
          : // Editing current schedule
            effectiveDate && effectiveDate > new Date()
            ? // Future date selected - create new future schedule
              {
                currentSchedule: values.wintSalarySpecificSettings?.schedule?.currentSchedule
                  ? {
                      ...values.wintSalarySpecificSettings.schedule.currentSchedule,
                      validFrom: values.wintSalarySpecificSettings.schedule.currentSchedule.validFrom
                        ? DateHelper.convertDateToShortISOString(
                            new Date(values.wintSalarySpecificSettings.schedule.currentSchedule.validFrom as any)
                          )
                        : DateHelper.convertDateToShortISOString(new Date()),
                    }
                  : undefined,
                futureSchedule: {
                  validFrom: DateHelper.convertDateToShortISOString(effectiveDate),
                  workRate: (values.employmentRate ?? 100) / 100,
                  standardWorkTime: (values.usualWorkHours ?? 40) * 60,
                  scheduleWeeks: currentScheduleWeeks,
                },
              }
            : effectiveDate
              ? // Immediate change - update currentSchedule
                {
                  currentSchedule: {
                    validFrom: DateHelper.convertDateToShortISOString(effectiveDate),
                    workRate: (values.employmentRate ?? 100) / 100,
                    standardWorkTime: (values.usualWorkHours ?? 40) * 60,
                    scheduleWeeks: currentScheduleWeeks,
                  },
                  futureSchedule: values.futureSchedule
                    ? {
                        validFrom: values.wintSalarySpecificSettings?.schedule?.futureSchedule?.validFrom
                          ? DateHelper.convertDateToShortISOString(
                              new Date(values.wintSalarySpecificSettings.schedule.futureSchedule.validFrom as any)
                            )
                          : DateHelper.convertDateToShortISOString(new Date()),
                        workRate: (values.futureEmploymentRate ?? 100) / 100,
                        standardWorkTime: (values.futureUsualWorkHours ?? 40) * 60,
                        scheduleWeeks: futureScheduleWeeks,
                      }
                    : null,
                }
              : // No effectiveDate - use current schedule from form if available
                currentScheduleWeeks && currentScheduleWeeks.length > 0
                ? {
                    currentSchedule: {
                      validFrom: values.wintSalarySpecificSettings?.schedule?.currentSchedule?.validFrom
                        ? DateHelper.convertDateToShortISOString(
                            new Date(values.wintSalarySpecificSettings.schedule.currentSchedule.validFrom as any)
                          )
                        : values.employmentStartDate
                          ? // Take the earlier of employment start date or today to ensure schedule is always current
                            DateHelper.convertDateToShortISOString(
                              new Date(Math.min(new Date(values.employmentStartDate).getTime(), new Date().getTime()))
                            )
                          : DateHelper.convertDateToShortISOString(new Date()),
                      workRate: (values.employmentRate ?? 100) / 100,
                      standardWorkTime: (values.usualWorkHours ?? 40) * 60,
                      scheduleWeeks: currentScheduleWeeks,
                    },

                    futureSchedule:
                      values.futureSchedule && values.wintSalarySpecificSettings?.schedule?.futureSchedule
                        ? {
                            validFrom: values.wintSalarySpecificSettings.schedule.futureSchedule.validFrom
                              ? DateHelper.convertDateToShortISOString(
                                  new Date(values.wintSalarySpecificSettings.schedule.futureSchedule.validFrom as any)
                                )
                              : DateHelper.convertDateToShortISOString(new Date()),
                            workRate: (values.futureEmploymentRate ?? 100) / 100,
                            standardWorkTime: (values.futureUsualWorkHours ?? 40) * 60,
                            scheduleWeeks: futureScheduleWeeks,
                          }
                        : null,
                  }
                : values.wintSalarySpecificSettings?.schedule,
      };
    }

    const salarySettingsPut: WintSuperkollAPIModel.EmployeeSalarySettingPut = {
      isActive: values.salaryActive ?? false,
      bankAccount: values.bankAccount,
      bankAccountClearing: values.bankAccountClearing,
      taxTable: values.taxTable ?? 0,
      employmentStartDate: values.employmentStartDate,
      employmentEndDate: values.employmentEndDate,
      terminationReason: values.employmentEndDate ? values.terminationReason : undefined,
      monthlySalary: isWintSalarySettingsAvailable && !requiresMonthly ? undefined : values.monthlySalary,
      employmentRate: values.employmentRate ?? 100,
      salaryChangeFrom:
        values.monthlySalary !== defaultValues?.monthlySalary || values.hourlySalary !== defaultValues?.hourlySalary
          ? (effectiveDate ?? values.salaryChangeFrom ?? getDefaultSalaryChangeDate(values.employmentStartDate))
          : undefined,
      workPlaceStreetAddress: values.workplaceStreetAddress,
      workPlaceCity: values.workplaceCity,
      employmentRateChangeFrom:
        values.employmentRate !== defaultValues?.employmentRate || (isWintSalarySettingsAvailable && effectiveDate)
          ? (effectiveDate ?? values.employmentRateChangeFrom)
          : // If user explicitly cleared the date (e.g., when canceling schedule), respect that
            values.employmentRateChangeFrom !== defaultValues?.employmentRateChangeFrom
            ? values.employmentRateChangeFrom
            : defaultValues?.employmentRateChangeFrom,
      position: values.position,
      vacationConditions:
        values.vacationConditions ?? getDefaultVacationConditions(values.employmentStartDate, values.wintSalaryType),
      workType: values.occupationalCategorisationId,
      usualWorkHours: values.usualWorkHours ?? 40,
      schedule:
        isWintSalarySettingsAvailable &&
        (values.employmentRate ?? 100) < 100 &&
        UtilsHelper.isEmpty(values.scheduleDescription)
          ? "-"
          : values.scheduleDescription,
      costCenter: values.salaryCostCenter?.length ? values.salaryCostCenter[0] : undefined,
      wintSalarySpecificSettings: wintSalarySpecificSettings,
    };

    if (employee && employee.id) {
      const promises: Promise<any>[] = [];
      if (canView(employee.id)) {
        promises.push(
          employeeService.updateEmployee(employee.id, {
            ...employeePut,
            pno: isWintSalarySettingsAvailable ? employeePut.pno : employee.pno,
          })
        );
        if (salaryAvailable) {
          promises.push(
            salaryService.updateEmployeeSalarySettings(
              employee.id,
              salarySettingsPut,
              Object.keys(SCHEDULE_VALIDATION_ERROR_MESSAGES)
            )
          );
        }
      }

      return await Promise.all(promises)
        .then(async (res) => {
          // Extract salary settings result (index 1 when salaryAvailable is true)
          const salarySettingsResult = salaryAvailable && res.length > 1 ? res[1] : undefined;

          await loadCompanyDataAsync(selectedCompanyId);

          // Reload employee data to update defaultValues and clear dirty state
          const freshEmployee = await employeeService.getEmployee(employee.id);
          const freshDefaults = getDefaultValues(freshEmployee);
          setDefaultValues(freshDefaults);

          // Show re-approve dialog if the salary change affected an approved period
          const reApproveData = salarySettingsResult?.reApprovalInfo;
          if (reApproveData) {
            const reApproveDialog = getReApproveDialog(
              reApproveData.personId,
              employee.name ?? "",
              reApproveData.yearMonth,
              false,
              undefined,
              undefined
            );
            setTimeout(() => {
              dispatch(displayDialog({ ...reApproveDialog }));
            }, 100);
          }

          return res;
        })
        .catch((error) => {
          const scheduleErrorCode = Array.isArray(error?.codes)
            ? error.codes.find((code: string) =>
                Object.prototype.hasOwnProperty.call(SCHEDULE_VALIDATION_ERROR_MESSAGES, code)
              )
            : undefined;
          if (scheduleErrorCode) {
            dispatch(
              displayErrorMessage(
                LocalizationHelperInstance.getFormattedMessage(SCHEDULE_VALIDATION_ERROR_MESSAGES[scheduleErrorCode]),
                { isGlobal: true }
              )
            );
          }
          throw error;
        });
    } else {
      const employeePost = employeePut as WintSuperkollAPIModel.EmployeePost;
      const permissionsIfAdminLoggedIn: WintSuperkollAPIModel.Permissions | undefined = employeePost.permissions && {
        ...employeePost.permissions,
        employee: true,
      };

      const permissionsIfSalaryAdminOrBusinessUnitManagerLoggedIn: WintSuperkollAPIModel.Permissions | undefined =
        employeePost.permissions && {
          ...employeePost.permissions,
          employee: true,
          receipts: true,
        };

      const permissions = props.isAdmin
        ? permissionsIfAdminLoggedIn
        : permissionsIfSalaryAdminOrBusinessUnitManagerLoggedIn;

      return employeeService
        .createEmployee({
          ...employeePost,
          mail: values.mail,
          permissions: permissions,
          salarySettings: salarySettingsPut as WintSuperkollAPIModel.EmployeeSalarySetting,
          businessUnitId: values.businessUnitId,
        })
        .then(async (res) => {
          await loadCompanyDataAsync(selectedCompanyId);

          if (props.isBusinessUnitManagerWithSalaryRights) {
            const updatedManageableEmployees = [...businessUnitManagerSettings.manageableEmployees, res];
            dispatch(
              updateBusinessUnitManagerSettings({
                ...businessUnitManagerSettings,
                manageableEmployees: updatedManageableEmployees,
              })
            );
          }

          // Navigate back to Settings/Employees list after creating
          const targetUrl = UrlHelper.formatPath(`/Settings?section=${SettingsConstants.NavigationKey.Employees}`);
          navigate(targetUrl);
        });
    }
  };

  const handleSubmit = async (values: EmployeeSettingsWithTemp) => {
    // A re-hire's schedule starts on the new start date; both dialogs below ask for an effective date
    // within the old employment.
    if (isRehire(values.employmentStartDate)) {
      return saveEmployee(values);
    }

    // Check if we're editing future schedule (from ref, not form values)
    const isEditingFutureSchedule = scheduleViewModeRef.current === "scheduled";

    // If editing future schedule, show dialog with existing date as default
    if (isEditingFutureSchedule && values.wintSalarySpecificSettings?.schedule?.futureSchedule) {
      const existingFutureDate = DateHelper.parseDateOnly(
        values.wintSalarySpecificSettings.schedule.futureSchedule.validFrom as any
      );
      const firstPossibleDate = values.wintSalarySpecificSettings?.schedule?.firstPossibleScheduleChangeDate
        ? DateHelper.parseDateOnly(values.wintSalarySpecificSettings.schedule.firstPossibleScheduleChangeDate as any)
        : undefined;

      return new Promise((resolve, reject) => {
        dispatch(
          displayDialog(
            getScheduleChangeDialog(
              (result) => {
                saveEmployee(values, result.startDate).then(resolve).catch(reject);
              },
              () => {
                reject(new Error("User cancelled"));
              },
              true,
              existingFutureDate,
              firstPossibleDate
            )
          )
        );
      });
    }

    // Check if any current Schedule tab fields have changed
    const scheduleChanged = JSON.stringify(values.schedule) !== JSON.stringify(defaultValues?.schedule);
    const employmentRateChanged = values.employmentRate !== defaultValues?.employmentRate;
    const usualWorkHoursChanged = values.usualWorkHours !== defaultValues?.usualWorkHours;

    const hasScheduleChanges = scheduleChanged || employmentRateChanged || usualWorkHoursChanged;
    const hasExistingFutureSchedule = !!values.futureSchedule;

    // Set default date to the first day of current month, or firstPossibleScheduleChangeDate if it's newer
    const firstOfMonth = DateHelper.getFirstDayOfMonth(DateHelper.today());
    const firstPossibleDate = values.wintSalarySpecificSettings?.schedule?.firstPossibleScheduleChangeDate
      ? DateHelper.parseDateOnly(values.wintSalarySpecificSettings.schedule.firstPossibleScheduleChangeDate as any)
      : undefined;

    // Use firstPossibleDate if it's newer than the first of the month
    const defaultDate = firstPossibleDate && firstPossibleDate > firstOfMonth ? firstPossibleDate : firstOfMonth;

    // Show dialog for WintSalary companies when updating the schedule
    if (isWintSalarySettingsAvailable && hasScheduleChanges && !isCreate) {
      return new Promise((resolve, reject) => {
        dispatch(
          displayDialog(
            getScheduleChangeDialog(
              (result) => {
                saveEmployee(values, result.startDate).then(resolve).catch(reject);
              },
              () => {
                reject(new Error("User cancelled"));
              },
              hasExistingFutureSchedule,
              defaultDate,
              firstPossibleDate
            )
          )
        );
      });
    }

    // No schedule changes or not WintSalary - save directly
    return saveEmployee(values);
  };

  React.useEffect(() => {
    setDefaultValues(getDefaultValues(props.employee));
    settingsService.getEmployeesSettings().then((res) => {
      const employees = res.employees
        ?.filter((emp) => emp.permissions?.incomingInvoices || emp.permissions?.invoices)
        .map(
          (emp): AutocompleteListItem => ({ id: emp.id, value: emp.name ?? "", icon: IconDefinitionConstants.WUser })
        );
      setHasIncomingInvoicesSignee(
        (res.employees?.filter((e) => e.permissions?.incomingInvoicesSignee) ?? []).length > 0
      );
      setHasReceiptsAdmin((res.employees?.filter((e) => e.permissions?.receiptsAdmin) ?? []).length > 0);

      employees && setAssistants(employees);
      props.employee && setLoading(false);
    });

    settingsService.getCompanySettings().then((res) => {
      setUserTypesEnabled(res.userTypesEnabled ?? false);
      setEnabledUserTypes(res.enabledUserTypes ?? []);
    }).catch(() => {
      setUserTypesEnabled(false);
      setEnabledUserTypes([]);
    });

    if (isWintSalarySettingsAvailable) {
      // Try to get salary types from employee payload first
      const payloadTypes = props.employee?.salarySettings?.wintSalarySpecificSettings?.availableSalaryTypes;

      if (payloadTypes && payloadTypes.length > 0) {
        // Use types from employee payload (existing employees)
        const types = payloadTypes.map((type: any) => ({
          name: type.name,
          description: type.description,
          optionallyRequiredFields: type.optionallyRequiredFields ?? 0,
        }));
        setAvailableSalaryTypes(types);
      } else if (selectedCompanyId) {
        // Fallback to API call if not in payload (new employees or backend stopped providing it)
        companyService
          .getAvailableSalaryTypes(selectedCompanyId)
          .then((salaryTypes) => {
            const types = (salaryTypes || []).map((type: any) => ({
              name: type.name,
              description: type.description,
              optionallyRequiredFields: type.optionallyRequiredFields ?? 0,
            }));
            setAvailableSalaryTypes(types);

            // Auto-select if only one salary type is available and no type is currently selected
            const currentSalaryType =
              props.employee?.salarySettings?.wintSalarySpecificSettings?.selectedSalaryType?.name;
            if (types.length === 1 && !currentSalaryType && formRef.current?.setFieldValue) {
              formRef.current.setFieldValue(FormFieldConstants.Settings.EmployeeSettings.WintSalaryType, types[0].name);
            }
          })
          .catch((error) => {
            console.error("Failed to load available salary types:", error);
            if (availableSalaryTypes.length === 0) {
              setAvailableSalaryTypes([]);
            }
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.employee?.id]); // Re-run when employee ID changes

  React.useEffect(() => {
    !loading &&
      isDefaultBusinessUnitRequired &&
      getBusinessUnitsWithSalaryRights().then((res) => {
        const businessUnit = res.length > 0 && res[0];
        if (businessUnit && formRef.current?.setFieldValue) {
          formRef.current.setValues &&
            formRef.current.setValues({
              businessUnitName: businessUnit.value,
              businessUnitId: businessUnit.id,
            });
        }
      });
  }, [props.employee, loading]);

  // Callback to update schedule view mode ref when user switches tabs
  const onScheduleViewModeChange = React.useCallback((mode: "current" | "scheduled") => {
    scheduleViewModeRef.current = mode;
  }, []);

  const handleEmploymentStartDateChange = React.useCallback(
    (fieldName: string, value: any, formikProps: FormikProps<SettingsFormTypes.EmployeeSettings>) => {
      formikProps.setFieldValue(fieldName, value);
      if (
        isWintSalarySettingsAvailable &&
        value &&
        availableSalaryTypes.length === 1 &&
        !formikProps.values.wintSalaryType
      ) {
        formikProps.setFieldValue(
          FormFieldConstants.Settings.EmployeeSettings.WintSalaryType,
          availableSalaryTypes[0].name
        );
      }

      // The re-hire becomes a new, open-ended employment. Keeping the old end date would give it an end
      // before its start, which Wint.Salary refuses, so the end date and reason belong to the old one. So
      // does a reason locked by the old employment's vacation settlement: the new one can take its own.
      if (canRehire) {
        const rehiring = isRehire(value);
        const terminationReason = rehiring ? undefined : defaultValues?.terminationReason;
        formikProps.setFieldValue(
          FormFieldConstants.Settings.EmployeeSettings.EmploymentEndDate,
          rehiring ? undefined : savedEndDate
        );
        formikProps.setFieldValue(FormFieldConstants.Settings.EmployeeSettings.TerminationReason, terminationReason);
        formikProps.setFieldValue(
          FormFieldConstants.Settings.EmployeeSettings.TerminationReasonName,
          getTerminationReasonName(terminationReason)
        );
        formikProps.setFieldValue(
          FormFieldConstants.Settings.EmployeeSettings.CanChangeTerminationReason,
          rehiring ? true : defaultValues?.canChangeTerminationReason
        );
      }
    },
    [isWintSalarySettingsAvailable, availableSalaryTypes, canRehire, savedStartDate, savedEndDate, defaultValues]
  );

  return loading ? (
    <Skeletons.FormSkeleton />
  ) : (
    <EmployeeForm
      ref={formRef}
      {...props}
      validationSchema={validationSchema}
      handleSubmit={handleSubmit}
      defaultValues={defaultValues}
      handleSpeedDial={handleQuickSelection}
      onPermissionChange={onPermissionChanged}
      onSalaryChange={onSalaryChange}
      isCreate={isCreate}
      isAssistantsActive={assistantsActive}
      assistants={assistants}
      onSalaryToggle={onSalaryToggle}
      getBusinessUnitsWithSalaryRights={getBusinessUnitsWithSalaryRights}
      salaryAvailable={salaryAvailable}
      includeCostCenterDimensionToSalary={includeCostCenterDimensionToSalary}
      employeeId={employee?.id}
      hasPersonNumber={!!employee?.pno}
      hasEmploymentStartDate={!!employee?.salarySettings?.employmentStartDate}
      canRehire={canRehire}
      isEmployee={isEmployee}
      availableSalaryTypes={availableSalaryTypes}
      userTypesEnabled={userTypesEnabled}
      enabledUserTypes={enabledUserTypes}
      onScheduleViewModeChange={onScheduleViewModeChange}
      onEmploymentStartDateChange={handleEmploymentStartDateChange}
    />
  );
};

export default forwardRef(EmployeeFormContainer);
